import React, { createContext, useContext, useReducer, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { BASE_URL } from "./Config";

// Cart Context
const CartContext = createContext();

// Cart Actions
const CART_ACTIONS = {
  SET_CART: "SET_CART",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
};

// Initial State
const initialState = {
  cart: {
    items: [],
    totalAmount: 0,
    itemCount: 0,
    restaurant: null,
  },
  loading: false,
  error: null,
};

// Reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.SET_CART:
      return {
        ...state,
        cart: action.payload,
        loading: false,
        error: null,
      };
    case CART_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case CART_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case CART_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Cart Service Class
class CartService {
  constructor() {
    this.baseURL = `${BASE_URL}/cart`;
    this.sessionId = this.getOrCreateSessionId();
  }

  getOrCreateSessionId() {
    let sessionId = localStorage.getItem("cart_session_id");
    if (!sessionId) {
      sessionId = uuidv4();
      localStorage.setItem("cart_session_id", sessionId);
    }
    return sessionId;
  }

  getAuthToken() {
    return localStorage.getItem("token");
  }

  getHeaders() {
    const headers = {
      "Content-Type": "application/json",
    };

    const token = this.getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }

  async makeRequest(endpoint, method = "GET", data = null) {
    try {
      const config = {
        method,
        headers: this.getHeaders(),
      };

      if (data) {
        config.body = JSON.stringify({
          ...data,
          sessionId: this.sessionId,
        });
      }

      const response = await fetch(`${this.baseURL}${endpoint}`, config);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Something went wrong");
      }

      return result;
    } catch (error) {
      console.error("Cart API Error:", error);
      throw error;
    }
  }

  async getCart() {
    return this.makeRequest("/get", "POST", {});
  }

  async addToCart(foodId, quantity = 1) {
    return this.makeRequest("/add", "POST", { foodId, quantity });
  }

  async updateCartItem(foodId, quantity) {
    return this.makeRequest("/update", "PUT", { foodId, quantity });
  }

  async removeFromCart(foodId) {
    return this.makeRequest("/remove", "DELETE", { foodId });
  }

  async clearCart() {
    return this.makeRequest("/clear", "DELETE", {});
  }

  async mergeCart() {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Authentication required for cart merge");
    }
    return this.makeRequest("/merge", "POST", {});
  }

  clearSession() {
    localStorage.removeItem("cart_session_id");
    this.sessionId = this.getOrCreateSessionId();
  }
}

// Cart Provider Component
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const cartService = new CartService();

  // Track authentication state to trigger merge
  const [prevAuthState, setPrevAuthState] = React.useState(null);

  // Load cart on mount
  useEffect(() => {
    loadCart();
  }, []);

  // Listen for authentication changes and merge cart when user logs in
  useEffect(() => {
    const handleAuthChange = async () => {
      const token = localStorage.getItem("token");
      const currentAuthState = !!token;
      
      // If user just logged in (was not authenticated, now is)
      if (prevAuthState === false && currentAuthState === true) {
        console.log("User logged in, attempting to merge cart...");
        try {
          await mergeCart();
        } catch (error) {
          console.error("Failed to merge cart:", error);
          // Still load the cart even if merge fails
          await loadCart();
        }
      } else if (prevAuthState === true && currentAuthState === false) {
        // User logged out - reload cart to get guest cart
        console.log("User logged out, reloading cart...");
        await loadCart();
      } else if (prevAuthState === null) {
        // Initial load - just load cart normally
        await loadCart();
      }
      
      setPrevAuthState(currentAuthState);
    };

    // Listen for storage changes (when user logs in/out in another tab)
    window.addEventListener("storage", handleAuthChange);
    
    // Listen for custom login/logout events
    window.addEventListener("userLoggedIn", handleAuthChange);
    window.addEventListener("logout", handleAuthChange);

    // Check initial auth state
    const initialToken = localStorage.getItem("token");
    setPrevAuthState(!!initialToken);

    return () => {
      window.removeEventListener("storage", handleAuthChange);
      window.removeEventListener("userLoggedIn", handleAuthChange);
      window.removeEventListener("logout", handleAuthChange);
    };
  }, [prevAuthState]);

  const loadCart = async () => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const response = await cartService.getCart();
      dispatch({ type: CART_ACTIONS.SET_CART, payload: response.data });
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  const addToCart = async (foodId, quantity = 1) => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const response = await cartService.addToCart(foodId, quantity);
      dispatch({ type: CART_ACTIONS.SET_CART, payload: response.data });
      return response;
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const updateQuantity = async (foodId, quantity) => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const response = await cartService.updateCartItem(foodId, quantity);
      dispatch({ type: CART_ACTIONS.SET_CART, payload: response.data });
      return response;
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const removeFromCart = async (foodId) => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const response = await cartService.removeFromCart(foodId);
      dispatch({ type: CART_ACTIONS.SET_CART, payload: response.data });
      return response;
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const response = await cartService.clearCart();
      dispatch({ type: CART_ACTIONS.SET_CART, payload: response.data });
      return response;
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const mergeCart = async () => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const response = await cartService.mergeCart();
      dispatch({ type: CART_ACTIONS.SET_CART, payload: response.data });
      console.log("Cart merged successfully:", response.message);
      return response;
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
      console.error("Cart merge failed:", error);
      throw error;
    }
  };

  const clearError = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_ERROR });
  };

  const getItemQuantity = (foodId) => {
    const item = state.cart.items.find((item) => item.food._id === foodId);
    return item ? item.quantity : 0;
  };

  const contextValue = {
    // State
    cart: state.cart,
    loading: state.loading,
    error: state.error,

    // Actions
    loadCart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    mergeCart,
    clearError,
    getItemQuantity,

    // Utilities
    cartService,
  };

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export default CartProvider;